// =====================================================
// VENUE API SERVICE
// Handles all API calls to the backend venue services
// =====================================================

import { fetchVenueRecordCached } from '../utils/venueRecordCache';
import { applyVenueContactOverrides } from '../utils/venueContactOverrides';
import {
  mergeAlcoholSettingsIntoAmenities,
  normalizeVenueAmenitiesObject,
  syncHostRulesWithAlcoholSettings
} from '../utils/bookingHostRules';
import { isHostPhotoGalleryImageRecord, resolveStoredHostPhotoUrl } from '../utils/hostPhotoUrl';
import { toCustomerError } from '../utils/customerError';
import { API_BASE_URL } from '../config/apiEndpoints';

class VenueApiService {
  constructor() {
    // Cache for search results
    this.searchCache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }
  /**
   * Fetch all venues from the API
   * @returns {Promise<Array>} Array of venue objects
   */
  async getAllVenues({ forceRefresh = false } = {}) {
    try {
      const url = forceRefresh
        ? `${API_BASE_URL}/venues?_=${Date.now()}`
        : `${API_BASE_URL}/venues`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(toCustomerError('', { status: response.status }));
      }
      
      const venues = await response.json();

      if (!Array.isArray(venues)) {
        return [];
      }
      
      // Transform API data to match frontend expectations
      return venues.map(venue => {
        try {
          return this.transformVenueData(venue);
        } catch (transformError) {
          return null;
        }
      }).filter(Boolean); // Remove any null entries
      
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch a specific venue by ID
   * @param {string} venueId - The venue ID
   * @returns {Promise<Object>} Venue object with detailed information
   */
  async getVenueById(venueId, { forceRefresh = false } = {}) {
    try {
      const venue = await fetchVenueRecordCached(
        venueId,
        async (id) => {
          const response = await fetch(`${API_BASE_URL}/venues/${id}`);

          if (!response.ok) {
            if (response.status === 404) {
              throw new Error(`Venue with ID ${id} not found`);
            }
            throw new Error(toCustomerError('', { status: response.status }));
          }

          return await response.json();
        },
        { forceRefresh }
      );

      return this.transformVenueData(venue);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Search venues based on location and filters
   * @param {Object} searchParams - Search parameters
   * @returns {Promise<Array>} Array of filtered venue objects
   */
  async searchVenues(searchParams = {}) {
    try {
      // Create cache key from search parameters
      const cacheKey = JSON.stringify(searchParams);
      const now = Date.now();
      
      // Check if we have cached results that are still valid
      if (this.searchCache.has(cacheKey)) {
        const cached = this.searchCache.get(cacheKey);
        if (now - cached.timestamp < this.cacheTimeout) {
          return cached.data;
        } else {
          // Remove expired cache entry
          this.searchCache.delete(cacheKey);
        }
      }
      
      // Build query parameters for backend search
      const queryParams = new URLSearchParams();
      
      // Add location filter to backend query
      if (searchParams.location && searchParams.location.toLowerCase() !== 'current location') {
        queryParams.append('location', searchParams.location);
      }
      
      // Add event type filter to backend query
      if (searchParams.eventType) {
        queryParams.append('eventType', searchParams.eventType);
      }
      
      // Add price range filter to backend query
      if (searchParams.priceRange) {
        if (searchParams.priceRange.min !== undefined) {
          queryParams.append('minPrice', searchParams.priceRange.min);
        }
        if (searchParams.priceRange.max !== undefined) {
          queryParams.append('maxPrice', searchParams.priceRange.max);
        }
      }
      
      // Add vibe filter to backend query
      if (searchParams.vibe) {
        queryParams.append('vibe', searchParams.vibe);
      }
      
      // Make API call with query parameters
      const queryString = queryParams.toString();
      const url = queryString ? 
        `${API_BASE_URL}/venues?${queryString}` : 
        `${API_BASE_URL}/venues`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(toCustomerError('', { status: response.status }));
      }
      
      const venues = await response.json();

      if (!Array.isArray(venues)) {
        return [];
      }
      
      // Transform API data to match frontend expectations
      const transformedVenues = venues.map(venue => {
        try {
          return this.transformVenueData(venue);
        } catch (transformError) {
          return null;
        }
      }).filter(Boolean); // Remove any null entries
      
      // Apply any remaining client-side filters that the backend doesn't support
      let filteredVenues = transformedVenues;
      
      // Handle "Current location" - get user's location and filter venues within 50 miles
      if (searchParams.location && searchParams.location.toLowerCase() === 'current location') {
        try {
          // Get user's current location
          const position = await this.getCurrentPosition();
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          filteredVenues = filteredVenues.filter(venue => {
            if (!venue.latitude || !venue.longitude) return false;
            
            const distance = this.calculateDistance(
              userLat, userLng, 
              venue.latitude, venue.longitude
            );
            
            return distance <= 50; // Within 50 miles
          });
        } catch (error) {
          // Keep all venues if location fails
        }
      }
      
      // Cache the results
      this.searchCache.set(cacheKey, {
        data: filteredVenues,
        timestamp: now
      });
      
      // Clean up old cache entries (keep only last 10 searches)
      if (this.searchCache.size > 10) {
        const oldestKey = this.searchCache.keys().next().value;
        this.searchCache.delete(oldestKey);
      }
      
      return filteredVenues;
      
    } catch (error) {
      // Fallback to client-side filtering if backend search fails
      try {
        const allVenues = await this.getAllVenues();
        return this.applyClientSideFilters(allVenues, searchParams);
      } catch (fallbackError) {
        return [];
      }
    }
  }

  /**
   * Get user's current position using geolocation API
   * @returns {Promise<Object>} Position object with coordinates
   */
  getCurrentPosition() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => reject(error),
        { timeout: 10000, enableHighAccuracy: true }
      );
    });
  }

  /**
   * Calculate distance between two coordinates using Haversine formula
   * @param {number} lat1 - First latitude
   * @param {number} lon1 - First longitude
   * @param {number} lat2 - Second latitude
   * @param {number} lon2 - Second longitude
   * @returns {number} Distance in miles
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   * @param {number} degrees - Degrees to convert
   * @returns {number} Radians
   */
  toRadians(degrees) {
    return degrees * (Math.PI/180);
  }

  /**
   * Apply client-side filters as fallback
   * @param {Array} venues - Array of venues to filter
   * @param {Object} searchParams - Search parameters
   * @returns {Array} Filtered venues
   */
  applyClientSideFilters(venues, searchParams) {
    let filteredVenues = venues;
    
    // Filter by location
    if (searchParams.location) {
      filteredVenues = this.filterByLocation(filteredVenues, searchParams.location);
    }
    
    // Filter by event type
    if (searchParams.eventType) {
      filteredVenues = this.filterByEventType(filteredVenues, searchParams.eventType);
    }
    
    // Filter by price range
    if (searchParams.priceRange) {
      filteredVenues = this.filterByPrice(filteredVenues, searchParams.priceRange);
    }
    
    // Filter by vibe
    if (searchParams.vibe) {
      filteredVenues = this.filterByVibe(filteredVenues, searchParams.vibe);
    }
    
    return filteredVenues;
  }

  /**
   * Transform API venue data to match frontend expectations
   * @param {Object} apiVenue - Raw venue data from API
   * @returns {Object} Transformed venue data
   */
  transformVenueData(apiVenue) {
    const normalizedVenue = applyVenueContactOverrides(apiVenue);

    const venue = {
      // Basic info
      id: normalizedVenue.id,
      name: normalizedVenue.name,
      city: normalizedVenue.city,
      area: normalizedVenue.address, // API uses 'address' field as area
      address: normalizedVenue.address, // API provides 'address' field directly
      description: normalizedVenue.description,
      
      // Contact info
      contact_name: normalizedVenue.contact_name,
      contact: normalizedVenue.contact || normalizedVenue.contact_name,
      contact_email: normalizedVenue.contact_email,
      contact_phone: normalizedVenue.contact_phone,
      owner_id: normalizedVenue.owner_id || normalizedVenue.owner?.id || null,
      owner: normalizedVenue.owner || null,
      /** Shown next to “Hosted by” on the venue page (e.g. CloudFront URL of an image in S3). */
      host_photo_url: (() => {
        const raw =
          apiVenue.host_photo_url ||
          apiVenue.owner_photo_url ||
          apiVenue.contact_photo_url ||
          null;
        const resolved = resolveStoredHostPhotoUrl(raw, {
          venueId: apiVenue.id,
          images: apiVenue.images
        });
        if (resolved == null || !String(resolved).trim()) return null;
        return String(resolved).trim();
      })(),
      
      // Pricing
      hourlyRate: apiVenue.hourly_rate,
      hourly_rate: apiVenue.hourly_rate, // Keep both for compatibility
      minimumBookingHours: apiVenue.minimum_booking_hours,
      
      // Capacity and features
      capacity: apiVenue.max_capacity || apiVenue.capacity, // API uses 'max_capacity' field
      maxCapacity: apiVenue.max_capacity || apiVenue.capacity, // Keep both for compatibility
      type: apiVenue.venue_type, // API uses 'venue_type'
      kidFriendly: apiVenue.kid_friendly,
      organizesEvents: apiVenue.organizes_events,
      rating: apiVenue.rating,
      
      // Location
      coordinates: {
        lat: apiVenue.latitude,
        lng: apiVenue.longitude
      },
      latitude: apiVenue.latitude,
      longitude: apiVenue.longitude,
      
      // Arrays
      vibes: apiVenue.vibes || [],
      eventTypes: apiVenue.event_types || [],
      facilities: apiVenue.facilities || [],
      platforms: apiVenue.platforms || [],
      
      // Spaces
      spaces: apiVenue.spaces || [],
      
      serves_alcohol: apiVenue.serves_alcohol,
      outside_alcohol_allowed: apiVenue.outside_alcohol_allowed,
      corkage_fee_for_byo: apiVenue.corkage_fee_for_byo,

      // Amenities (complex object) - normalize API structure for frontend expectations
      ...(() => {
        const alcoholSource = {
          serves_alcohol: apiVenue.serves_alcohol,
          outside_alcohol_allowed: apiVenue.outside_alcohol_allowed,
          corkage_fee_for_byo: apiVenue.corkage_fee_for_byo,
          amenities: apiVenue.amenities,
        };
        let normalizedAmenities = normalizeVenueAmenitiesObject(apiVenue.amenities);
        if (normalizedAmenities) {
          normalizedAmenities = mergeAlcoholSettingsIntoAmenities(
            normalizedAmenities,
            alcoholSource
          );
          const syncedHostRules = syncHostRulesWithAlcoholSettings(
            normalizedAmenities.eventRules?.hostRules ?? [],
            alcoholSource
          );
          normalizedAmenities.eventRules = {
            ...normalizedAmenities.eventRules,
            hostRules: syncedHostRules,
            host_rules: syncedHostRules,
          };
          normalizedAmenities.event_rules = normalizedAmenities.eventRules;
        }
        return {
          amenities: normalizedAmenities,
          host_rules: normalizedAmenities?.eventRules?.hostRules ?? [],
        };
      })(),
      
      // Images - API returns full images array for individual venues, primary_image_url for listings
      ...(() => {
        const hostPhotoUrl =
          apiVenue.host_photo_url ||
          apiVenue.owner_photo_url ||
          apiVenue.contact_photo_url ||
          null;
        const galleryImages = (apiVenue.images || []).filter(
          (img) => !isHostPhotoGalleryImageRecord(img, hostPhotoUrl)
        );
        return {
          images: galleryImages,
          primaryImageUrl:
            apiVenue.primary_image_url ||
            galleryImages.find((img) => img.is_primary)?.image_url ||
            galleryImages[0]?.image_url ||
            null,
          primaryImageType:
            apiVenue.primary_image_type ||
            (galleryImages.length > 0
              ? galleryImages.find((img) => img.is_primary)?.image_type
              : null),
        };
      })(),
      
      // Pricing visibility - when true, hide price in search, details, booking
      hide_price:
        apiVenue.hide_price != null
          ? Boolean(apiVenue.hide_price)
          : apiVenue.hide_pricing != null
            ? Boolean(apiVenue.hide_pricing)
            : null,
      stripe_payments_enabled: Boolean(apiVenue.stripe_payments_enabled),
      platform_fee_enabled: apiVenue.platform_fee_enabled !== false,
      deposit_enabled: Boolean(apiVenue.deposit_enabled),
      deposit_percent:
        apiVenue.deposit_percent != null && apiVenue.deposit_percent !== ''
          ? Number(apiVenue.deposit_percent)
          : null,
      stripe_checkout_enabled:
        apiVenue.stripe_checkout_enabled != null
          ? Boolean(apiVenue.stripe_checkout_enabled)
          : null,
      payment_link: apiVenue.payment_link || apiVenue.payment_url || '',

      // Additional fields from API
      postal_code: apiVenue.postal_code,
      standing_capacity: apiVenue.standing_capacity,
      recurring_discount: apiVenue.recurring_discount,
      off_peak_discount: apiVenue.off_peak_discount,
      availability_notes: apiVenue.availability_notes,
      is_active: apiVenue.is_active,
      is_verified: apiVenue.is_verified,
      is_partner: apiVenue.is_partner,
      isPartner: apiVenue.is_partner,
      
      // Timestamps
      createdAt: apiVenue.created_at,
      updatedAt: apiVenue.updated_at,

      /** Full cancellation copy; may duplicate amenities.event_rules.cancellationPolicy */
      cancellation_policy: apiVenue.cancellation_policy ?? null
    };

    return venue;
  }

  /**
   * Filter venues by location
   * @param {Array} venues - Array of venues
   * @param {string} location - Location search term
   * @returns {Array} Filtered venues
   */
  filterByLocation(venues, location) {
    const searchTerm = location.toLowerCase();
    
    // Handle "Current location" - for now, return all venues
    if (searchTerm === 'current location') {
      return venues;
    }
    
    // Filter by city
    return venues.filter(venue => 
      venue.city.toLowerCase().includes(searchTerm) ||
      venue.area.toLowerCase().includes(searchTerm) ||
      venue.name.toLowerCase().includes(searchTerm)
    );
  }

  /**
   * Filter venues by event type
   * @param {Array} venues - Array of venues
   * @param {string} eventType - Event type to filter by
   * @returns {Array} Filtered venues
   */
  filterByEventType(venues, eventType) {
    return venues.filter(venue => 
      (venue.event_types || venue.eventTypes || []).includes(eventType.toLowerCase())
    );
  }

  /**
   * Filter venues by price range
   * @param {Array} venues - Array of venues
   * @param {Object} priceRange - Price range object with min/max
   * @returns {Array} Filtered venues
   */
  filterByPrice(venues, priceRange) {
    return venues.filter(venue => {
      const hourlyRate = venue.hourly_rate || venue.hourlyRate || 0;
      
      if (priceRange.min !== undefined && priceRange.max !== undefined) {
        return hourlyRate >= priceRange.min && hourlyRate < priceRange.max;
      } else if (priceRange.min !== undefined) {
        return hourlyRate >= priceRange.min;
      } else if (priceRange.max !== undefined) {
        return hourlyRate < priceRange.max;
      } else {
        return true;
      }
    });
  }

  /**
   * Filter venues by vibe
   * @param {Array} venues - Array of venues
   * @param {string} vibe - Vibe to filter by
   * @returns {Array} Filtered venues
   */
  filterByVibe(venues, vibe) {
    return venues.filter(venue => 
      (venue.vibes || []).includes(vibe.toLowerCase())
    );
  }

  /**
   * Get venue images for a specific venue
   * @param {string} venueId - The venue ID
   * @returns {Promise<Array>} Array of image objects
   */
  async getVenueImages(venueId) {
    try {
      const venue = await this.getVenueById(venueId);
      
      // For now, return the primary image as an array
      // TODO: Update API to return all images for a venue
      const images = [];
      
      if (venue.primaryImageUrl) {
        images.push({
          id: `${venueId}-primary`,
          url: venue.primaryImageUrl,
          type: venue.primaryImageType || 'primary',
          isPrimary: true,
          alt: `${venue.name} - Primary image`
        });
      }
      
      return images;
      
    } catch (error) {
      return [];
    }
  }

  /**
   * Get venues owned by a specific user (by contact email)
   * @param {string} ownerEmail - The venue owner's email
   * @returns {Promise<Array>} Array of venue objects owned by the user
   */
  async getVenuesByOwner(ownerEmail) {
    try {
      const allVenues = await this.getAllVenues();
      const normalizedOwner = String(ownerEmail || '').toLowerCase();
      return allVenues.filter(
        (venue) =>
          venue.contact_email &&
          venue.contact_email.toLowerCase() === normalizedOwner
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch dynamic pricing for a specific venue
   * @param {string} venueId - The venue ID
   * @returns {Promise<Array>} Array of dynamic pricing rules
   */
  async getDynamicPricing(venueId) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/dynamic-pricing`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(toCustomerError('', { status: response.status }));
      }
      
      const pricingData = await response.json();

      return Array.isArray(pricingData) ? pricingData : [];

    } catch (error) {
      // Return empty array instead of throwing to avoid breaking the venue details page
      return [];
    }
  }

  /**
   * Get flat pricing rules for a venue (for booking price calculation)
   * @param {string} venueId - Venue ID
   * @param {string} [spaceId] - Optional space ID
   * @returns {Promise<Array>} Flat pricing rules { min_hours, flat_price, ... }
   */
  async getFlatPricing(venueId, spaceId = null) {
    try {
      const url = spaceId
        ? `${API_BASE_URL}/venues/${venueId}/flat-pricing?space_id=${spaceId}`
        : `${API_BASE_URL}/venues/${venueId}/flat-pricing`;
      const response = await fetch(url);
      if (!response.ok) {
        if (response.status === 404) return [];
        throw new Error(toCustomerError('', { status: response.status }));
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Get booking rules (min hours for full venue and per-person)
   * @param {string} venueId - Venue ID
   * @returns {Promise<Object|null>} { min_hours_exclusive, min_hours_per_person, ... }
   */
  /**
   * Public customer feedback for a venue page. Only the fields shown on the page are kept.
   * @param {string} venueId
   * @returns {Promise<{ count: number, average_rating: number|null, feedback: Array }>}
   */
  async getVenueFeedback(venueId) {
    const empty = { count: 0, average_rating: null, feedback: [] };
    if (!venueId) return empty;
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/feedback`);
      if (!response.ok) return empty;
      const data = await response.json();
      const feedback = Array.isArray(data?.feedback) ? data.feedback : [];
      return {
        count: Number.isFinite(Number(data?.count)) ? Number(data.count) : feedback.length,
        average_rating: data?.average_rating ?? null,
        feedback: feedback.map((row) => ({
          id: row.id || row.booking_id,
          rating: row.rating,
          comment: row.comment,
          submitted_at: row.submitted_at,
          booking_date: row.booking_date,
          event_name: row.event_name,
        })),
      };
    } catch (error) {
      return empty;
    }
  }

  /**
   * Public booking rows for past-event cards. Contact details are dropped here.
   * @param {string} venueId
   * @returns {Promise<Array>}
   */
  async getVenueBookings(venueId) {
    if (!venueId) return [];
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings?venue_id=${encodeURIComponent(venueId)}`
      );
      if (!response.ok) return [];
      const data = await response.json();
      const bookings = Array.isArray(data)
        ? data
        : (Array.isArray(data?.bookings) ? data.bookings : []);
      return bookings.map((booking) => ({
        id: booking.id,
        event_name: booking.event_name,
        event_type: booking.event_type,
        guest_count: booking.guest_count,
        headcount: booking.headcount,
        booking_date: booking.booking_date,
        status: booking.status,
      }));
    } catch (error) {
      return [];
    }
  }

  async getBookingRules(venueId) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/booking-rules`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(toCustomerError('', { status: response.status }));
      }
      return response.json();
    } catch (error) {
      return null;
    }
  }
}

// Create and export a singleton instance
const venueApiService = new VenueApiService();
export default venueApiService;
